import { defineStore } from 'pinia'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { computed, ref } from 'vue'

import { api } from '@/lib/api'
import { getPatientToken } from '@/lib/auth-token'
import { fetchItem } from '@/lib/lingodb'
import { isNative } from '@/lib/platform'
import {
  downloadItemMedia,
  isItemComplete,
  loadExerciseManifest,
  pruneExerciseManifest,
  pruneOrphanItems,
  saveExerciseManifest,
} from '@/lib/exercise-media-cache'
import { useResumableRunsStore } from '@/stores/resumableRuns'
import type { DownloadStatus, Exercise } from '@/stores/exercises'

/**
 * Patient-side homework list + offline media-download pipeline.
 *
 * Homework is the patient-app view of therapist-assigned exercises flagged as
 * `is_homework` (see app/Http/Controllers/Patient/HomeworkController). It is a
 * near-twin of {@link useExercisesStore} — same `Exercise` shape, same media
 * cache pipeline — but:
 *
 *   • the source is `GET /api/patient/homework` (therapist exercises), not
 *     `/api/patient/exercises`;
 *   • there is NO `destroy()` — the therapist owns homework, the patient can't
 *     delete it. The list is reconciled to the server on every refresh, so an
 *     un-assigned / un-flagged exercise simply drops off (and its cache is
 *     pruned);
 *   • all cache I/O is under the `'homework'` namespace so `therapist_exercises.id`
 *     can't clobber a patient-authored `exercises.id` of the same number (the two
 *     are independent autoincrement spaces). See lib/exercise-media-cache.ts.
 *
 * The id in an `Exercise` here is the THERAPIST exercise id; the drill is
 * launched via `/training?homework=<id>` and runs upload to
 * `/api/patient/homework/{id}/runs`.
 */

/** Cache namespace shared by the media cache + resumable-runs for homework. */
export const HOMEWORK_NAMESPACE = 'homework'
const HOMEWORK_INDEX_FILE = 'lingodb/homework.json'
const FRESH_WINDOW_MS = 10 * 60 * 1000
const PER_EXERCISE_CONCURRENCY = 4

export const useHomeworkStore = defineStore('homework', () => {
  const exercises = ref<Exercise[]>([])
  const downloadStatus = ref<Record<number, DownloadStatus>>({})
  const status = ref<'idle' | 'hydrating' | 'loading' | 'ready' | 'error'>('idle')
  const lastFetchedAt = ref<Date | null>(null)
  const error = ref<string | null>(null)

  let inflight: Promise<void> | null = null
  const downloadInflight = new Map<number, Promise<void>>()

  const isReady = computed(() => status.value === 'ready')

  function getById(id: number): Exercise | undefined {
    return exercises.value.find((e) => e.id === id)
  }

  function isFresh(): boolean {
    if (lastFetchedAt.value === null) return false
    return Date.now() - lastFetchedAt.value.getTime() < FRESH_WINDOW_MS
  }

  // --- Index file (instant cold-launch render) ---------------------------

  async function loadDiskIndex(): Promise<Exercise[]> {
    if (!isNative) return []
    try {
      const { data } = await Filesystem.readFile({
        path: HOMEWORK_INDEX_FILE,
        directory: Directory.Data,
        encoding: 'utf8' as never,
      })
      const text = typeof data === 'string' ? data : await (data as Blob).text()
      const parsed = JSON.parse(text)
      return Array.isArray(parsed) ? (parsed as Exercise[]) : []
    } catch {
      return []
    }
  }

  async function saveDiskIndex(list: Exercise[]): Promise<void> {
    if (!isNative) return
    await Filesystem.writeFile({
      path: HOMEWORK_INDEX_FILE,
      data: JSON.stringify(list),
      directory: Directory.Data,
      encoding: 'utf8' as never,
      recursive: true,
    })
  }

  // --- Download pipeline -------------------------------------------------

  async function statusFromManifest(ex: Exercise): Promise<DownloadStatus> {
    if (!isNative) return 'ready'
    const manifest = await loadExerciseManifest(ex.id, HOMEWORK_NAMESPACE)
    const allComplete = ex.item_ids.every((id) => isItemComplete(manifest[id]))
    return allComplete ? 'ready' : 'preparing'
  }

  /**
   * Download whatever is still missing for one homework exercise. Identical
   * shape to {@link useExercisesStore}'s pipeline, only namespaced. Persists
   * the manifest after every item so a kill mid-download keeps real progress.
   */
  async function enqueueDownload(ex: Exercise): Promise<void> {
    const existing = downloadInflight.get(ex.id)
    if (existing) return existing

    const run = (async () => {
      if (!isNative) {
        downloadStatus.value = { ...downloadStatus.value, [ex.id]: 'ready' }
        return
      }
      downloadStatus.value = { ...downloadStatus.value, [ex.id]: 'preparing' }

      const manifest = await loadExerciseManifest(ex.id, HOMEWORK_NAMESPACE)
      const todo = ex.item_ids.filter((id) => !isItemComplete(manifest[id]))
      if (todo.length === 0) {
        downloadStatus.value = { ...downloadStatus.value, [ex.id]: 'ready' }
        return
      }

      let failed = false
      let cursor = 0
      const next = (): number | undefined => (cursor < todo.length ? todo[cursor++] : undefined)

      async function worker(): Promise<void> {
        for (let id = next(); id !== undefined; id = next()) {
          try {
            const result = await fetchItem(id)
            const itemFull = result.body?.data
            if (itemFull === undefined) continue

            let updated = await downloadItemMedia(itemFull, manifest[id], HOMEWORK_NAMESPACE)

            if (!updated.complete) {
              const retry = await fetchItem(id, undefined, { bustCache: true })
              const fresh = retry.body?.data
              if (fresh !== undefined) {
                updated = await downloadItemMedia(fresh, updated, HOMEWORK_NAMESPACE)
              }
            }

            manifest[id] = updated
            await saveExerciseManifest(ex.id, manifest, HOMEWORK_NAMESPACE)

            if (!updated.complete) failed = true
          } catch (e) {
            console.warn(`homework ${ex.id}: item ${id} fetch failed:`, e)
            failed = true
          }
        }
      }

      const workers = Array.from(
        { length: Math.min(PER_EXERCISE_CONCURRENCY, todo.length) },
        () => worker(),
      )
      await Promise.all(workers)

      const allComplete = ex.item_ids.every((id) => isItemComplete(manifest[id]))
      downloadStatus.value = {
        ...downloadStatus.value,
        [ex.id]: allComplete ? 'ready' : failed ? 'error' : 'preparing',
      }
    })()
      .finally(() => {
        downloadInflight.delete(ex.id)
      })

    downloadInflight.set(ex.id, run)
    return run
  }

  // --- Public actions ----------------------------------------------------

  async function refresh(): Promise<void> {
    status.value = exercises.value.length > 0 ? 'hydrating' : 'loading'
    error.value = null
    try {
      // Ids we knew about before this refresh — so homework the therapist has
      // un-assigned / un-flagged can have its on-disk cache reclaimed.
      const priorIds = new Set(exercises.value.map((e) => e.id))

      const { data } = await api.get<{ data: Exercise[] }>('/patient/homework')
      const list = data.data

      exercises.value = list
      lastFetchedAt.value = new Date()
      status.value = 'ready'

      await saveDiskIndex(list)

      const newIds = new Set(list.map((e) => e.id))
      for (const id of priorIds) {
        if (!newIds.has(id)) {
          await pruneExerciseManifest(id, HOMEWORK_NAMESPACE)
          // A "weitermachen" tab for vanished homework would be misleading.
          await useResumableRunsStore().drop(id, HOMEWORK_NAMESPACE)
        }
      }

      const seeded: Record<number, DownloadStatus> = { ...downloadStatus.value }
      for (const ex of list) {
        seeded[ex.id] = await statusFromManifest(ex)
      }
      downloadStatus.value = seeded
      await pruneOrphanItems(unionItemIds(list), HOMEWORK_NAMESPACE)

      for (const ex of list) {
        if (downloadStatus.value[ex.id] !== 'ready') {
          await enqueueDownload(ex)
        }
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      status.value = exercises.value.length > 0 ? 'ready' : 'error'
    }
  }

  async function preload(): Promise<void> {
    if (inflight) return inflight
    if (isFresh()) return

    inflight = (async () => {
      try {
        if (exercises.value.length === 0) {
          status.value = 'hydrating'
          const cached = await loadDiskIndex()
          if (cached.length > 0) {
            exercises.value = cached
            const seeded: Record<number, DownloadStatus> = {}
            for (const ex of cached) {
              seeded[ex.id] = await statusFromManifest(ex)
            }
            downloadStatus.value = seeded
          }
        }

        // Refresh only with auth (homework needs a paired patient anyway).
        if (isNative) {
          const token = await getPatientToken()
          if (!token) {
            status.value = exercises.value.length > 0 ? 'ready' : 'idle'
            return
          }
        }

        await refresh()
      } finally {
        inflight = null
      }
    })()

    return inflight
  }

  return {
    // state
    exercises,
    downloadStatus,
    status,
    lastFetchedAt,
    error,
    // getters
    isReady,
    // actions
    preload,
    refresh,
    enqueueDownload,
    getById,
  }
})

function unionItemIds(list: Exercise[]): number[] {
  const set = new Set<number>()
  for (const ex of list) for (const id of ex.item_ids) set.add(id)
  return Array.from(set)
}
